import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const ProjectBurndownChart = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data || !data.startDate || !data.endDate) return [];

    const { totalEstimationPoints, startDate, endDate, completedPointsByDate } = data;

    // 1. Setup the initial Date boundaries
    const start = new Date(startDate);
    const plannedEnd = new Date(endDate);
    const today = new Date();

    // 2. Calculate the "Ideal Burn" strictly based on planned dates
    const plannedMs = plannedEnd.getTime() - start.getTime();
    const plannedDays = Math.ceil(plannedMs / (1000 * 60 * 60 * 24)) || 1;
    const dailyBurn = totalEstimationPoints / plannedDays;

    // =========================================================
    // 🎯 THE FIX: Find the True End Date for the Chart's Canvas
    // =========================================================
    let chartEndDate = new Date(plannedEnd);

    // A. Check if any items were completed AFTER the planned end date
    const completionDates = Object.keys(completedPointsByDate).sort();
    if (completionDates.length > 0) {
      const lastCompletionDate = new Date(completionDates[completionDates.length - 1]);
      if (lastCompletionDate > chartEndDate) {
        chartEndDate = lastCompletionDate; // Stretch chart to the late completion
      }
    }

    // B. If points are STILL remaining, and today is past our chart end date, 
    // extend the canvas to today to show the flat-lining "Actual" trace.
    const totalCompletedPoints = Object.values(completedPointsByDate).reduce((sum, val) => sum + val, 0);
    if ((totalEstimationPoints - totalCompletedPoints) > 0 && today > chartEndDate) {
      chartEndDate = today;
    }

    // 3. Calculate the total days to render on the stretched canvas
    const chartTotalMs = chartEndDate.getTime() - start.getTime();
    const chartTotalDays = Math.ceil(chartTotalMs / (1000 * 60 * 60 * 24)) || 1;

    let pointsArray = [];
    let currentActualPoints = totalEstimationPoints;
    let currentIdealPoints = totalEstimationPoints;

    // 4. Loop through every day up to the new stretched chartEndDate
    for (let i = 0; i <= chartTotalDays; i++) {
      const currentDate = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      const dateStr = currentDate.toISOString().split('T')[0];
      const displayDate = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const pointsCompletedToday = completedPointsByDate[dateStr] || 0;
      currentActualPoints -= pointsCompletedToday;

      const isFuture = currentDate > today;

      pointsArray.push({
        date: displayDate,
        // Math.max(0) ensures the Ideal line flatlines at 0 even as the chart stretches further right
        ideal: Math.max(0, currentIdealPoints), 
        actual: isFuture ? null : currentActualPoints
      });

      // Reduce the ideal points (this will naturally drop below 0 after the plannedEnd date)
      currentIdealPoints -= dailyBurn;
    }

    return pointsArray;
  }, [data]);

  if (!data || !data.startDate) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <p>Not enough timeline data to generate a burndown chart.</p>
      </div>
    );
  }

  return (
    <div className="card chart-card" style={{ padding: '24px', minHeight: '450px' }}>
      <div className="chart-card-header" style={{ marginBottom: '20px' }}>
        <h3>Project Burndown Timeline</h3>
        <span className="sprint-total-badge">
          {data.totalEstimationPoints} Total Points
        </span>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
          
          {/* Added minTickGap to prevent dates from overlapping on long projects */}
          <XAxis 
             dataKey="date" 
             stroke="var(--ink-soft)" 
             tick={{ fill: 'var(--ink-soft)' }} 
             minTickGap={30} 
          />
          
          <YAxis stroke="var(--ink-soft)" tick={{ fill: 'var(--ink-soft)' }} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1e2128', border: 'none', borderRadius: '8px', color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
          <Legend verticalAlign="top" height={36} wrapperStyle={{ color: 'var(--ink-soft)' }} />
          
          <Line 
            name="Ideal Timeline"
            type="linear" 
            dataKey="ideal" 
            stroke="var(--ink-soft)" 
            strokeWidth={2}
            strokeDasharray="5 5" 
            dot={false} 
          />
          <Line 
            name="Actual Remaining Points"
            type="stepAfter" 
            dataKey="actual" 
            stroke="var(--accent-teal)" 
            strokeWidth={3} 
            dot={{ r: 3, fill: 'var(--accent-teal)' }}
            activeDot={{ r: 6 }} 
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProjectBurndownChart;